import { useCallback } from 'react';

import { useLazyQuery } from '@apollo/client';

import { DEFAULT_SORTED_VALUE, NUMBER_OF_RESULTS_PER_PAGE } from 'bundles/search-common/constants';
import type {
  SearchHitMetadataInputForSummary,
  SearchProductHit,
  SearchRequestOrigin,
} from 'bundles/search-common/providers/searchTypes';
import AI_SUMMARY_QUERY from 'bundles/search-common/queries/aiSummaryQuery.graphql';
import type { SearchRequest, SummarizeSearchResultsResponse } from 'bundles/search-common/utils/aiSummaryMocks';
import { convertSearchHitsToResultItems } from 'bundles/search-common/utils/aiSummaryMocks';

type UseAISummaryOptions = {
  onCompleted?: (data: SummarizeSearchResultsResponse) => void;
  onError?: (error: Error) => void;
};

type UseAISummaryReturn = {
  summarizeResults: (
    query: string,
    searchResults: SearchProductHit[],
    requestOrigin: SearchRequestOrigin,
    userContext?: { userId?: string; preferences?: string[] }
  ) => void;
  loading: boolean;
  error: Error | undefined;
  data: SummarizeSearchResultsResponse | undefined;
  called: boolean;
};

const useAISummary = (options: UseAISummaryOptions = {}): UseAISummaryReturn => {
  const [executeSummaryQuery, { loading, error, data, called }] = useLazyQuery<
    { SearchResult: { summarizeSearchResults: SummarizeSearchResultsResponse } },
    { originalSearchRequest: SearchRequest; topSearchResults: SearchHitMetadataInputForSummary[] }
  >(AI_SUMMARY_QUERY, {
    onCompleted: (result) => {
      if (result?.SearchResult?.summarizeSearchResults) {
        options.onCompleted?.(result.SearchResult.summarizeSearchResults);
      }
    },
    onError: (apolloError) => {
      options.onError?.(apolloError);
    },
    errorPolicy: 'all', // Continue even if there are GraphQL errors
    fetchPolicy: 'cache-and-network', // Always try to fetch fresh data but use cache as fallback
  });

  const summarizeResults = useCallback<
    (
      query: string,
      searchResults: SearchProductHit[],
      requestOrigin: SearchRequestOrigin,
      userContext?: { userId?: string; preferences?: string[] }
    ) => void
  >(
    (
      query: string,
      searchResults: SearchProductHit[],
      requestOrigin: SearchRequestOrigin,
      userContext?: { userId?: string; preferences?: string[] }
    ) => {
      if (!query.trim() || searchResults.length === 0) {
        return;
      }

      const topSearchResults: SearchHitMetadataInputForSummary[] = convertSearchHitsToResultItems(searchResults);

      const originalSearchRequest: SearchRequest = {
        entityType: 'PRODUCTS',
        query: query.trim(),
        limit: NUMBER_OF_RESULTS_PER_PAGE,
        maxValuesPerFacet: 1000,
        cursor: '0',
        sortBy: DEFAULT_SORTED_VALUE,
        disableRecommender: false,
        enableAutoAppliedFilters: false,
        explain: true,
        requestOrigin,
      };

      executeSummaryQuery({
        variables: {
          originalSearchRequest,
          topSearchResults,
        },
      });
    },
    [executeSummaryQuery]
  );

  return {
    summarizeResults,
    loading,
    error,
    data: data?.SearchResult?.summarizeSearchResults,
    called,
  };
};

export default useAISummary;
